import { isMessageContentValid } from '../../src/utils/isMessageContentValid.js'
import cookieConsentManager from '../services/cookie-consent-manager'
import { widgetManager } from '../../src/widgetManager/index.js'
import GenesysChatMessage from '/app/models/genesysChatMessage'
import QuickReplies from '../quickReplies'
import templates from '/app/templates'
import config from '/config/config.js'
import BaseChannel from './base'
import _ from 'underscore'

const DEFAULT_COUNTRY = 'pl'

const defaultTranslationsForVideoOffer = {
  videoOfferTitle: 'Video call',
  videoOfferDescription: 'The consultant offers you a video call.',
  videoOfferCanceledText: 'Request canceled',
  videoOfferAcceptedText: 'Accepted',
  videoOfferAcceptButton: 'Join video call',
  videoOfferDeclinedText: 'Declined',
  videoOfferDeclineButton: 'Decline',
}

const defaultTranslationsForChat = {
  submitPreInteractionFormText: 'Start chat',
  submitInInteractionFormText: 'Send',
}

const defaultTranslationsForAudioOffer = {
  audioOfferTitle: 'Audio call',
  audioOfferDescription: 'The consultant offers you a audio call.',
  audioOfferAcceptButton: 'Join audio call',
}

const defaultTranslationsForModals = {
  clearHistoryDescription: 'Are you sure you want to delete your chat history?',
  clearHistoryCancel: 'Cancel',
  clearHistorySubmit: 'Delete',
  clearHistoryComplete: 'Chat history has been deleted',
  transcriptClose: 'Close',
  transcriptEmailInputLabel: 'E-mail address',
  transcriptDescription: 'Send chat transcript to your e-mail',
  transcriptCancel: 'Cancel',
  transcriptSubmit: 'Delete',
  transcriptComplete: 'Chat history has been sent to your email address',
}

export default class GenesysWebMessengerChannel extends BaseChannel {
  constructor(
    settings,
    {
      agentImage,
      genesysWebMessengerHistoryService,
      billingService,
      widgetContainer,
      texts,
      isMobile,
      botImage,
      tokenService,
      triggers,
      widgetIndex,
      showNotification,
      preInteractionForm,
      validatePreInteractionForm,
      initPreInteractionForm,
      adjustPreInteractionFormConsentsStyles,
      interactionForms,
    }
  ) {
    super(settings, { widgetContainer })
    this.tokenService = tokenService
    this.texts = texts
      .filter((x) => x.type === 'chat')
      .reduce(function (map, obj) {
        map[obj.key] = obj.value
        return map
      }, {})
    Object.keys(defaultTranslationsForChat).forEach((key) => {
      if (!this.texts.hasOwnProperty(key)) {
        this.texts[key] = defaultTranslationsForChat[key]
      }
    })
    Object.keys(defaultTranslationsForVideoOffer).forEach((key) => {
      if (!this.texts.hasOwnProperty(key)) {
        this.texts[key] = defaultTranslationsForVideoOffer[key]
      }
    })
    Object.keys(defaultTranslationsForAudioOffer).forEach((key) => {
      if (!this.texts.hasOwnProperty(key)) {
        this.texts[key] = defaultTranslationsForAudioOffer[key]
      }
    })
    Object.keys(defaultTranslationsForModals).forEach((key) => {
      if (!this.texts.hasOwnProperty(key)) {
        this.texts[key] = defaultTranslationsForModals[key]
      }
    })
    this.cobrowseTexts = texts
      .filter((x) => x.type === 'cobrowse')
      .reduce(function (map, obj) {
        map[obj.key] = obj.value
        return map
      }, {})
    this.preInteractionFormTexts = texts
      .filter((x) => x.type === 'preInteractionForm')
      .reduce(function (map, obj) {
        map[obj.key] = obj.value
        return map
      }, {})
    this.genesysWebMessengerHistoryService = genesysWebMessengerHistoryService
    this.billingService = billingService
    this.showNotification = showNotification
    this.fields = settings.fields || []
    this.deploymentId = this.params.deployment
    this.configuration = this.params.configuration || {}
    this.hubConnection = {}

    this.autoStartEnabled = this.configuration['autoStart'] && this.configuration['autoStart'].toString() === 'true'

    this.clearConversationEnabled = this.configuration['conversationClear'] && this.configuration['conversationClear'].toString() === 'true'

    this.messengerInitialized = false
    this.websocketUrl = this.params.websocketUrl || 'wss://webmessaging.mypurecloud.de'
    this.websocketUrl = this.websocketUrl.replace(/\/$/, '')

    this.channelType = 'chat'
    this.agentimage = agentImage
    this.botimage = botImage
    this.lastCoBrowseImage = agentImage
    this.isMobile = isMobile()
    this.handleActiveCobrowseSession()
    this.isActive = false
    this.requestId = ''

    this.typingIndicatorTimeout = null
    this.sendTypingIndicatorTimeout = null
    this.currentSlides = {}

    this.interactionForms = interactionForms
    this.preInteractionForm = preInteractionForm
    this.preInteractionFormEnabled = false
    this.preInteractionFormExist = false
    this.validatePreInteractionForm = validatePreInteractionForm
    this.adjustPreInteractionFormConsentsStyles = adjustPreInteractionFormConsentsStyles
    this.initPreInteractionForm = initPreInteractionForm
    this.filerCountriesText = texts.find((x) => x.key === 'filterCountries')?.value || 'Filter countries'
    this.selectedFormType = ''
    this.preInteractionFormData = null

    this.preInteractionFormIti = {}
    if (preInteractionForm && Object.keys(preInteractionForm).length > 0 && !this.autoStartEnabled) {
      this.preInteractionFormEnabled = preInteractionForm.chat.enabled
      const chatForm = window.localStorage.getItem('cce-widget-chat-form-data')

      if (chatForm) {
        this.preInteractionFormExist = true
        this.preInteractionFormData = JSON.parse(chatForm)
      }
    }

    this.actionEventSent = false
    this.onChannelSelected = this.fields[0].onChannelSelected
    this.handleUpgradeChat = this.fields[0].handleUpgradeChat
    this.headsUpType = this.fields[0].headsUpType
    this.isQuickRepliesActive = this.fields[0].isQuickRepliesActive
    this.triggers = triggers
    this.headsUpQuickRepliesFristInitalize = false
    this.headsUpQuickRepliesOpened = false
    this.showChatTranscript = this.fields[0].showChatTranscript

    this.imageExtensions = ['image/jpg', 'image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/webp', 'image/svg+xml', 'image/svg']
    this.usingPredictiveEngagement = settings.fields[0].usingPredictiveEngagement
    this.widgetIndex = widgetIndex
  }

  removeCoBrowseInfoPanel() {
    if (document.getElementById('cce-widget-chat__co-browse-control-request-popup-info')) {
      document.getElementById('cce-widget-chat__co-browse-control-request-popup-info').remove()
    }
  }

  showCoBrowseInfo() {
    this.removeCoBrowseInfoPanel()
    let coBrowseControlRequestInfo = templates.chatCoBrowseControlRequestInfoTemplate({
      styleNamespace: this.styleNamespace,
      type: 'CoBrowseInfo',
      texts: this.cobrowseTexts,
    })
    document.body.insertAdjacentHTML('beforeend', coBrowseControlRequestInfo)
    document.getElementById('cce-widget-chat__cobrowse-info-stop').addEventListener('click', () => {
      Genesys('command', 'CobrowseService.stopSession')
      this.removeCoBrowseInfoPanel()
    })
  }

  showCoBrowseControlRequestInfo() {
    this.removeCoBrowseInfoPanel()
    let coBrowseControlRequest = templates.chatCoBrowseControlRequestInfoTemplate({
      styleNamespace: this.styleNamespace,
      type: 'CoBrowse-ControlRequest',
      texts: this.cobrowseTexts,
    })
    document.body.insertAdjacentHTML('beforeend', coBrowseControlRequest)
    document.getElementById('cce-widget-chat__cobrowse-control-accept').addEventListener('click', () => {
      Genesys('command', 'CobrowseService.acceptControl')
      this.showCoBrowseControlGrantedInfo()
    })
    document.getElementById('cce-widget-chat__cobrowse-control-decline').addEventListener('click', () => {
      Genesys('command', 'CobrowseService.declineControl')
      this.showCoBrowseInfo()
    })
  }

  showCoBrowseControlGrantedInfo() {
    this.removeCoBrowseInfoPanel()
    let coBrowseControlRequestInfo = templates.chatCoBrowseControlRequestInfoTemplate({
      styleNamespace: this.styleNamespace,
      type: 'Cobrowse-ControlGranted',
      texts: this.cobrowseTexts,
    })
    document.body.insertAdjacentHTML('beforeend', coBrowseControlRequestInfo)
    document.getElementById('cce-widget-chat__cobrowse-control-revoke').addEventListener('click', () => {
      Genesys('command', 'CobrowseService.declineControl')
      this.showCoBrowseInfo()
    })
    document.getElementById('cce-widget-chat__cobrowse-info-stop').addEventListener('click', () => {
      Genesys('command', 'CobrowseService.stopSession')
      this.removeCoBrowseInfoPanel()
    })
  }

  handleActiveCobrowseSession() {
    var activeCobrowse = localStorage.getItem('cce-genesys-active-cobrowse')
    if (activeCobrowse != 'null' && activeCobrowse != null) {
      if (typeof Genesys === 'undefined') {
        this.initCoBrowse(this.params.deployment, this.params.environment, this.params.regionShort)
      }
      this.showCoBrowseInfo()
    }
  }

  setup() {
    this.styleNamespace = 'cce-widget-chat'
  }

  open() {
    this.$element = document.createElement('div')
    this.$element.classList.add(this.styleNamespace)
    this.$quickReplayElement = document.createElement('div')
    this.$quickReplayElement.classList.add('this.styleNamespace')

    this.render()

    // TODO: Check exactly what `isActive` is used for. Currently, notifications don't work after refresh if isActive is true.
    // const widgetWindow = document.querySelector('.cce-widget__window')
    // if (widgetWindow.classList.contains('cce-widget__window--open')) {
    //   this.isActive = true
    // }
  }

  render() {
    this.$element.innerHTML = templates.chatTemplate(this.getTemplateVariables())
    if (this.preInteractionFormEnabled && !this.preInteractionFormExist) {
      const chatForm = window.localStorage.getItem('cce-widget-chat-form-data')
      if (!chatForm) {
        this.$element.innerHTML += templates.preInteractionFormTemplate({
          form: this.preInteractionForm.chat,
          styleNamespace: this.styleNamespace,
          submitButtonText: this.texts.submitPreInteractionFormText,
          texts: this.preInteractionFormTexts,
          additionalClassname: '',
          formDataType: 'chat',
        })
      }
    }

    this.observeEvents()

    if (this.widgetContainer.settings.widgets[this.widgetIndex].customAttributes) {
      this.widgetContainer.settings.widgets[this.widgetIndex].customAttributes.forEach((attribute) => {
        window.trueengage.addCustomAttribute(attribute.key, attribute.value)
      })
    }

    setTimeout(async () => {
      if (this.preInteractionFormEnabled) {
        this.preInteractionFormIti.form = this.initPreInteractionForm(
          'chat',
          this.params.defaultCountry || this.widgetContainer.settings.countryCode || DEFAULT_COUNTRY,
          this.filerCountriesText
        )
      }
      await this.initWebMessenger()
    }, 0)
    this.observeState()
  }

  observeState() {
    // Options for the observer (which mutations to observe)
    const config = { attributes: true, attributeOldValue: true }
    const widgetId = this.widgetContainer.settings.widgets[this.widgetIndex].id

    // Callback function to execute when mutations are observed
    const callback = (mutationsList) => {
      for (let mutation of mutationsList) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const targetNode = document.querySelector('.cce-widget-chat')
          let currentClassState = targetNode.classList.contains('open')
          if (currentClassState) {
            //channel chat opened
            const widgetNode = document.querySelector('.cce-widget__window')
            if (widgetNode && widgetNode.classList.contains('cce-widget__window--open')) {
              this.scrollMessages()
              console.debug('Chat channel is opened && widget is opened')
              cookieConsentManager.setLocalStorageItem(`trueengage-widget-opened-${widgetId}`, 'true', 'necessary')
              this.setChannelActive()
              const notificationDivs = document.querySelectorAll('.chat__notification')
              if (localStorage.getItem('trueengage-widget-minimized') === 'false' || !localStorage.getItem('trueengage-widget-minimized')) {
                notificationDivs.forEach((notificationDiv) => {
                  notificationDiv.textContent = '0'
                  notificationDiv.classList.remove('chat__notification-show')
                })
              }
              if (localStorage.getItem('trueengage-widget-minimized') === 'false' || !localStorage.getItem('trueengage-widget-minimized')) {
                cookieConsentManager.setLocalStorageItem('lastChatInteraction', new Date(), 'necessary')
              }
            } else {
              console.debug('Chat channel is opened BUT widget is not opened')
            }
          }
          this.adjustPreInteractionFormConsentsStyles('chat')
        }
      }
    }

    // Create an instance of MutationObserver with the callback function
    const observer = new MutationObserver(callback)

    // Start observing the target node for configured mutations
    observer.observe(this.$element, config)
  }

  setChannelActive() {
    const activeChannel = {
      type: 'chat',
      id: this.id,
    }
    this.widgetContainer.setActiveChannel(activeChannel)
  }

  formatDateToDaysHours(date) {
    const formattedDateTime = date.toLocaleString('en-US', {
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    return formattedDateTime
  }

  getTemplateVariables() {
    const storedMessages = localStorage.getItem('cce-genesys-messages')
    let conversationStart = ''
    if (storedMessages) {
      const messages = JSON.parse(storedMessages)
      const firstMessageTimestamp = new Date(messages[0].timestamp)
      conversationStart = this.formatDateToDaysHours(firstMessageTimestamp)
    }

    return {
      styleNamespace: this.styleNamespace,
      agreementQuestions: [],
      additionalFields: this.fields || [],
      deploymentId: this.params.deployment,
      texts: this.texts,
      conversationStart: conversationStart,
    }
  }

  generateUUID() {
    var d = new Date().getTime() //Timestamp
    var d2 = (typeof performance !== 'undefined' && performance.now && performance.now() * 1000) || 0 //Time in microseconds since page-load or 0 if unsupported
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 //random number between 0 and 16
      if (d > 0) {
        r = (d + r) % 16 | 0
        d = Math.floor(d / 16)
      } else {
        r = (d2 + r) % 16 | 0
        d2 = Math.floor(d2 / 16)
      }
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
    })
  }

  appendQuickReplyOptionsFromHistory(msg, saveToCache = true, headsUp = true) {
    let generatedId = `qr-${msg.id}`

    if (!msg.content) return
    let options = []
    msg.content.forEach((qr) => {
      if (qr.contentType === 'QuickReply' && _.isObject(qr.quickReply)) {
        let payload = qr.quickReply.payload
        let text = qr.quickReply.text
        let action = qr.quickReply.action

        options.push({ text, payload, action })
      }
    })
    if (headsUp) {
      this.quickReplies = new QuickReplies({
        quickReplayHeaderText: this.handleUrls(msg.text),
        widgetContainer: this.container,
        onChannelSelected: () => this.onChannelSelected(),
      })
      document.querySelector('.cce-widget-container').append(this.quickReplies.$element)
      if (!this.headsUpQuickRepliesFristInitalize) {
        this.quickReplies.render()
        this.runAfterTrigger(true)
        this.headsUpQuickRepliesFristInitalize = true
      }

      let headsUpMessageTemplate = templates.headsUpQuickReplyTemplate({
        options: options,
        direction: msg.direction,
        timestamp: this.formatDate(msg.channel.time),
        styleNamespace: this.styleNamespace,
        id: generatedId,
        headsUp: true,
      })

      const quickRepliesContainer = document.querySelector('.cce-widget__quick-replies__container')
      if (quickRepliesContainer) {
        const children = Array.from(quickRepliesContainer.children)
        children.forEach((child) => {
          if (!child.classList.contains('cce-widget__quick-replies__container__question')) {
            quickRepliesContainer.removeChild(child)
          } else {
            child.innerHTML = this.handleUrls(msg.text)
          }
        })

        quickRepliesContainer.insertAdjacentHTML('beforeend', headsUpMessageTemplate)
      }
    }
    let messageTemplate = templates.chatQuickReplyTemplate({
      options: options,
      direction: msg.direction,
      timestamp: this.formatDate(msg.channel.time),
      styleNamespace: this.styleNamespace,
      id: generatedId,
    })

    this.$element.querySelector('#cce-widget-chat__messages').insertAdjacentHTML('beforeend', messageTemplate)

    document.querySelectorAll(`#${generatedId} .${this.styleNamespace}__quick-reply`).forEach((element) =>
      element.addEventListener('click', () => {
        widgetManager.openWidget()

        let payload = element.dataset.payload
        let text = element.innerText
        if (element.classList.contains('headsup-reply')) {
          this.onChannelSelected()
        }
        let socketMsg = {
          action: 'onMessage',
          token: this.token,
          message: {
            type: 'Text',
            content: [
              {
                contentType: 'ButtonResponse',
                buttonResponse: {
                  text: text,
                  type: 'QuickReply',
                  payload: payload,
                },
              },
            ],
          },
        }

        this.appendMessage(text, 'self', new Date().toISOString())

        this.socket.send(JSON.stringify(socketMsg))
        this.scrollMessages()

        this.isActive = true

        const siblings = (elem) => {
          let siblings = []

          if (!elem.parentNode) {
            return siblings
          }

          let sibling = elem.parentNode.firstElementChild

          do {
            if (sibling != elem) {
              siblings.push(sibling)
            }
          } while ((sibling = sibling.nextElementSibling))

          return siblings
        }

        const allQuickReplies = document.querySelectorAll(`#${generatedId} .${this.styleNamespace}__quick-reply`)

        allQuickReplies.forEach((reply) => {
          reply.classList.add(`${this.styleNamespace}__quick-reply__inactive`)
          reply.setAttribute('disabled', 'true')
          if (reply.dataset.payload === payload) {
            reply.classList.add(`${this.styleNamespace}__quick-reply__chosen`)
          }
        })

        const nodes = siblings(element)

        nodes.forEach((elem) => {
          elem.classList.add(`${this.styleNamespace}__quick-reply__inactive`)
          elem.setAttribute('disabled', 'true')
        })

        element.classList.add(`${this.styleNamespace}__quick-reply__chosen`)
        element.classList.add(`${this.styleNamespace}__quick-reply__inactive`)
        element.setAttribute('disabled', 'true')
      })
    )
  }

  async appendCarousel(cards, direction) {
    let generatedId = `carousel-${this.generateUUID()}`

    const imagePromises = cards.map((card) => {
      return new Promise((resolve) => {
        let image = new Image()
        image.onload = function () {
          resolve()
        }
        image.onerror = function () {
          card.image = ''
          resolve()
        }
        image.src = card.image
      })
    })

    await Promise.all(imagePromises)

    let carouselTemplate = templates.chatCarouselTemplate({
      cards: cards,
      direction: direction,
      //timestamp: this.formatDate(msg.channel.time),
      styleNamespace: this.styleNamespace,
      id: generatedId,
      type: `card${cards.length}`,
      isMobile: this.isMobile,
    })

    this.$element.querySelector('#cce-widget-chat__messages').insertAdjacentHTML('beforeend', carouselTemplate)

    setTimeout(() => {
      window.swiffyslider.init()
    }, 0)

    this.currentSlides[generatedId] = 1

    const updateButtonsState = (generatedId, cardsLength) => {
      const prevButton = document.querySelector(`.previous-${generatedId}`)
      const nextButton = document.querySelector(`.next-${generatedId}`)

      prevButton.disabled = this.currentSlides[generatedId] <= 1
      nextButton.disabled = this.currentSlides[generatedId] >= cardsLength
    }

    const handleSlideNavigation = (generatedId, cardsLength, direction) => {
      if (direction === 'prev' && this.currentSlides[generatedId] > 1) {
        this.currentSlides[generatedId] -= 1
      } else if (direction === 'next' && this.currentSlides[generatedId] < cardsLength) {
        this.currentSlides[generatedId] += 1
      }

      updateButtonsState(generatedId, cardsLength)
    }

    document.querySelector(`.previous-${generatedId}`).addEventListener('click', () => {
      handleSlideNavigation(generatedId, cards.length, 'prev')
    })

    document.querySelector(`.next-${generatedId}`).addEventListener('click', () => {
      handleSlideNavigation(generatedId, cards.length, 'next')
    })

    updateButtonsState(generatedId, cards.length)
    this.scrollMessages()

    document.querySelectorAll(`#${generatedId} .${this.styleNamespace}__carousel_card_btn`).forEach((element) =>
      element.addEventListener('click', () => {
        let payload = element.dataset.payload
        let text = element.innerText

        let socketMsg = {
          action: 'onMessage',
          token: this.token,
          message: {
            type: 'Structured',
            text: text,
            content: [
              {
                contentType: 'ButtonResponse',
                buttonResponse: {
                  text: text,
                  type: 'Button',
                  payload: payload,
                },
              },
            ],
          },
        }

        this.appendMessage(text, 'self', new Date().toISOString())

        this.socket.send(JSON.stringify(socketMsg))
        this.scrollMessages()

        this.isActive = true
      })
    )
  }

  appendQuickReplyOptions(options, direction, timestamp, saveToCache = true, headsUp = false, headsUpHeaderText) {
    let generatedId = `qr-${this.generateUUID()}`

    if (headsUp && this.headsUpQuickRepliesFristInitalize === false) {
      this.quickReplies = new QuickReplies({
        quickReplayHeaderText: this.handleUrls(headsUpHeaderText),
        widgetContainer: this.container,
        onChannelSelected: () => this.onChannelSelected(),
      })
      document.querySelector('.cce-widget-container').append(this.quickReplies.$element)

      const isWidgetOpen = document.querySelector('.cce-widget__window').classList.contains('cce-widget__window--open')
      if (
        isWidgetOpen &&
        document.querySelector('.cce-widget-chat').classList.contains('open') &&
        !document.querySelector('.cce-widget-container ').classList.contains('minimized')
      ) {
        cookieConsentManager.setLocalStorageItem('lastChatInteraction', new Date(), 'necessary')
      }
      if (!this.headsUpQuickRepliesFristInitalize && !isWidgetOpen) {
        this.quickReplies.render()
        this.runAfterTrigger(true)
        this.headsUpQuickRepliesFristInitalize = true
      }

      let headsUpMessageTemplate = templates.headsUpQuickReplyTemplate({
        options: options,
        direction: direction,
        timestamp: this.formatDate(timestamp),
        styleNamespace: this.styleNamespace,
        id: generatedId,
        headsUp: true,
      })

      const quickRepliesContainer = document.querySelector('.cce-widget__quick-replies__container')
      if (quickRepliesContainer) {
        quickRepliesContainer.insertAdjacentHTML('beforeend', headsUpMessageTemplate)
      }
    }
    let messageTemplate = templates.chatQuickReplyTemplate({
      options: options,
      direction: direction,
      timestamp: this.formatDate(timestamp),
      styleNamespace: this.styleNamespace,
      id: generatedId,
    })

    this.$element.querySelector('#cce-widget-chat__messages').insertAdjacentHTML('beforeend', messageTemplate)

    document.querySelectorAll(`#${generatedId} .${this.styleNamespace}__quick-reply`).forEach((element) =>
      element.addEventListener('click', () => {
        widgetManager.openWidget()

        let payload = element.dataset.payload
        let text = element.innerText
        if (element.classList.contains('headsup-reply')) {
          this.onChannelSelected()
        }
        let socketMsg = {
          action: 'onMessage',
          token: this.token,
          message: {
            type: 'Text',
            content: [
              {
                contentType: 'ButtonResponse',
                buttonResponse: {
                  text: text,
                  type: 'QuickReply',
                  payload: payload,
                },
              },
            ],
          },
        }

        this.appendMessage(text, 'self', new Date().toISOString())

        this.socket.send(JSON.stringify(socketMsg))
        this.scrollMessages()

        this.isActive = true

        const siblings = (elem) => {
          let siblings = []

          if (!elem.parentNode) {
            return siblings
          }

          let sibling = elem.parentNode.firstElementChild

          do {
            if (sibling != elem) {
              siblings.push(sibling)
            }
          } while ((sibling = sibling.nextElementSibling))

          return siblings
        }

        const allQuickReplies = document.querySelectorAll(`#${generatedId} .${this.styleNamespace}__quick-reply`)

        allQuickReplies.forEach((reply) => {
          reply.classList.add(`${this.styleNamespace}__quick-reply__inactive`)
          reply.setAttribute('disabled', 'true')
          if (reply.dataset.payload === payload) {
            reply.classList.add(`${this.styleNamespace}__quick-reply__chosen`)
          }
        })

        const nodes = siblings(element)

        nodes.forEach((elem) => {
          elem.classList.add(`${this.styleNamespace}__quick-reply__inactive`)
          elem.setAttribute('disabled', 'true')
        })

        element.classList.add(`${this.styleNamespace}__quick-reply__chosen`)
        element.classList.add(`${this.styleNamespace}__quick-reply__inactive`)
        element.setAttribute('disabled', 'true')
      })
    )
  }

  appendDisconnectStatusInfo(timestamp) {
    const hangUpButton = document.querySelector('#cce-widget-video__hangup')
    if (hangUpButton) hangUpButton.click()

    let statusInfo = templates.genesysWebMessengerStatusMessage({
      message: this.texts.disconnectMessage,
      timestamp: this.formatDate(timestamp),
      styleNamespace: this.styleNamespace,
    })

    this.$element.querySelector('#cce-widget-chat__messages').insertAdjacentHTML('beforeend', statusInfo)
  }

  appendUpgradeRequest(channelType, history = false, type = 'Offering') {
    if (this.requestId) {
      return
    }
    var requestId = this.generateUUID()
    let videoOffer = templates.chatVideoOfferTemplate({
      id: requestId,
      texts: this.texts,
      timestamp: this.formatDate(new Date()),
      styleNamespace: this.styleNamespace,
      type: type,
      history: history,
      channelType: channelType,
    })

    this.$element.querySelector('#cce-widget-chat__messages').insertAdjacentHTML('beforeend', videoOffer)

    if (type == 'Offering' && history == false) {
      this.requestId = requestId
      var acceptButton = document.getElementById('cce-widget-chat__video-offer-accept')
      var rejectButton = document.getElementById('cce-widget-chat__video-offer-reject')
      this.addProgressBar(60).then(async () => {
        await this.rejectVideoOffer(requestId)
      })
      acceptButton.addEventListener('click', () => this.upgradeToVideo(requestId, channelType))
      rejectButton.addEventListener('click', () => this.rejectVideoOffer(requestId, channelType))
    }

    this.scrollMessages()
  }

  async upgradeToVideo(requestId, channelType) {
    let message
    if (channelType === 'AudioOffer') {
      message = 'TE_AUDIO_REQUEST_ACCEPTED'
    } else {
      message = 'TE_VIDEO_REQUEST_ACCEPTED'
    }
    this.requestId = ''
    await this.sendMessage(message)
    document.getElementById(`${requestId}`).remove()
    document.querySelector('button[name=ccewBack]').click()
    this.handleUpgradeChat(channelType)
  }

  async rejectVideoOffer(requestId, channelType) {
    let message
    if (channelType === 'AudioOffer') {
      message = 'TE_AUDIO_REQUEST_REJECTED'
    } else {
      message = 'TE_VIDEO_REQUEST_REJECTED'
    }
    const roomId = 'reject'
    await this.hubConnection.invoke('SendRoomId', this.widgetContainer.settings.id, this.widgetContainer.token, roomId)
    this.requestId = ''
    await this.sendMessage(message)
    document.getElementById(`${requestId}`).remove()
  }

  appendCoBrowseRequest(sessionId, sessionJoinToken, type, direction, timestamp, history, agentPhoto) {
    let generatedId = `co-browse-${sessionId}`
    if (!agentPhoto) {
      agentPhoto = this.lastCoBrowseImage
    } else {
      this.lastCoBrowseImage = agentPhoto
    }
    let coBrowseRequest = templates.chatCoBrowseRequestTemplate({
      message: 'co-browse request',
      direction: direction,
      timestamp: this.formatDate(timestamp),
      styleNamespace: this.styleNamespace,
      sessionId: sessionId,
      sessionJoinToken: sessionJoinToken,
      type: type,
      id: generatedId,
      history: history,
      texts: this.cobrowseTexts,
      agentImage: agentPhoto,
    })

    if (type == 'OfferingAccepted' || type == 'OfferingRejected' || type == 'OfferingExpired') {
      const elementToRemove = this.$element.querySelector('#cce-widget-chat__messages').querySelector(`#${generatedId}`)
      if (elementToRemove) {
        elementToRemove.remove()
      }
    }
    this.$element.querySelector('#cce-widget-chat__messages').insertAdjacentHTML('beforeend', coBrowseRequest)

    if (type == 'Offering' && history == false) {
      this.addProgressBar(60)
    }

    document.querySelectorAll(`#${generatedId} .${this.styleNamespace}__coBrowse_btn`).forEach((element) =>
      element.addEventListener('click', () => {
        let accept = element.dataset.accept
        let sessionId = element.dataset.sessionid
        let sessionToken = element.dataset.sessiontoken
        if (typeof Genesys === 'undefined') {
          this.initCoBrowse(this.params.deployment, this.params.environment, this.params.regionShort)
        }
        if (accept == 'true') {
          let socketMsg = {
            action: 'onMessage',
            token: this.token,
            message: {
              type: 'Event',
              events: [
                {
                  eventType: 'CoBrowse',
                  coBrowse: {
                    type: 'OfferingAccepted',
                    sessionId: sessionId,
                    sessionJoinToken: sessionToken,
                  },
                },
              ],
            },
          }

          cookieConsentManager.setLocalStorageItem('cce-genesys-active-cobrowse', `${sessionId}|${sessionJoinToken}`, 'necessary')

          this.socket.send(JSON.stringify(socketMsg))
          this.scrollMessages()

          Genesys(
            'command',
            'CobrowseService.acceptSession',
            {
              joinCode: sessionToken,
              sessionId: sessionId,
            },
            () => {}
          )
          this.showCoBrowseInfo()
        } else if (accept == 'false') {
          let socketMsg = {
            action: 'onMessage',
            token: this.token,
            message: {
              type: 'Event',
              events: [
                {
                  eventType: 'CoBrowse',
                  coBrowse: {
                    type: 'OfferingRejected',
                    sessionId: sessionId,
                    sessionJoinToken: sessionToken,
                  },
                },
              ],
            },
          }
          this.socket.send(JSON.stringify(socketMsg))

          Genesys(
            'command',
            'CobrowseService.declineSession',
            {
              joinCode: sessionToken,
              sessionId: sessionId,
            },
            () => {}
          )
          cookieConsentManager.setLocalStorageItem('cce-genesys-active-cobrowse', null, 'necessary')
        }
      })
    )
  }

  addProgressBar(sec) {
    return new Promise((resolve) => {
      const progressbar = document.getElementById(`${this.styleNamespace}__progressbar`)
      progressbar.className = 'progressbar'

      const progressbarinner = document.createElement('div')
      progressbarinner.className = 'inner'
      progressbarinner.style.animationDuration = `${sec}s`

      progressbarinner.addEventListener('animationend', () => {
        resolve('Animation completed')
      })

      progressbar.appendChild(progressbarinner)
      progressbarinner.style.animationPlayState = 'running'
    })
  }

  handleUrls(msg) {
    if (msg.trim().startsWith('<img') || msg.trim().startsWith('<svg') || msg.trim().startsWith('<div')) {
      return msg
    }

    const urlRegex = /\[([^\]]+)\]\(([^)]+)\)|\b(?:https?|ftp):\/\/\S+/gi
    const matches = msg.matchAll(urlRegex)
    for (const match of matches) {
      const fullMatch = match[0]
      const linkText = match[1] || fullMatch // Jeśli jest format Markdown, użyj tekstu linku, w przeciwnym razie użyj pełnego URL-a
      const url = match[2] || fullMatch // Jeśli jest format Markdown, użyj URL-a, w przeciwnym razie użyj pełnego URL-a
      const linkElement = `<a class='${this.styleNamespace}__link' href="${url}" target="_blank">${linkText}</a>`
      msg = msg.replace(fullMatch, linkElement)
    }

    return msg
  }

  appendMessage(msg, direction, timestamp, agentPhoto, attachment, saveToCache = true) {
    if (msg == 'TE_VIDEO_REQUEST_ACCEPTED') {
      this.appendUpgradeRequest('VideoOffer', false, 'OfferingAccepted')
      return
    } else if (msg == 'TE_VIDEO_REQUEST_REJECTED') {
      this.appendUpgradeRequest('VideoOffer', false, 'OfferingRejected')
      return
    } else if (msg == 'TE_AUDIO_REQUEST_ACCEPTED') {
      this.appendUpgradeRequest('AudioOffer', false, 'OfferingAccepted')
      return
    } else if (msg == 'TE_AUDIO_REQUEST_REJECTED') {
      this.appendUpgradeRequest('AudioOffer', false, 'OfferingRejected')
      return
    }
    //remove typing indicator if exists
    const typingIndicator = this.$element.querySelector('#cce-widget-chat__msg-typing-indicator')
    if (typingIndicator) {
      typingIndicator.remove()
    }

    if (!agentPhoto) {
      agentPhoto = this.agentimage
    }
    if (direction === 'remote') {
      this.updateChannelAvatarDirectly(agentPhoto)
    }

    msg = this.handleUrls(msg)

    function parseMessage(msg, isAttachment = false) {
      if (isAttachment || msg.startsWith('TE_')) {
        return msg
      }
      const replacements = [
        { regex: /```([^`]+)```/g, class: 'formatPre' }, // ```pre```
        { regex: /\*([^*]+)\*/g, class: 'formatBold' }, // *bold*
        { regex: /_([^_]+)_/g, class: 'formatItalic' }, // _italic_
        { regex: /~([^~]+)~/g, class: 'formatStrike' }, // ~strike~
        { regex: /`([^`]+)`/g, class: 'formatHighlight' }, // `highlight`
      ]

      function applyReplacementsOutsideTags(msg) {
        const parts = msg.split(/(<[^>]+>)/g)
        const processedParts = parts.map((part) => {
          if (part.startsWith('<')) {
            return part
          }
          replacements.forEach(({ regex, class: className }) => {
            if (className === 'formatPre') {
              part = part.replace(regex, `<div class="${className}">$1</div>`)
            } else {
              part = part.replace(regex, `<span class="${className}">$1</span>`)
            }
          })
          return part
        })

        return processedParts.join('')
      }
      return applyReplacementsOutsideTags(msg)
    }

    let formattedMessage = parseMessage(msg, attachment ? true : false)
    let messageTemplate = templates.chatMessageTemplate({
      message: formattedMessage,
      direction: direction,
      timestamp: this.formatDate(timestamp),
      styleNamespace: this.styleNamespace,
      agentImage: agentPhoto,
      systemMessage: formattedMessage.startsWith('TE_'),
    })

    this.$element.querySelector('#cce-widget-chat__messages').insertAdjacentHTML('beforeend', messageTemplate)
    if (attachment) {
      document.querySelector(`.${this.styleNamespace}__attachment-${attachment.uniqueId}`).addEventListener('click', () => {
        this.getImgDownloadLink(attachment.url, attachment.fileName)
      })
    }

    if (saveToCache) {
      let savedMessages = localStorage.getItem('cce-genesys-messages')
      if (savedMessages === null) {
        savedMessages = []
      } else {
        savedMessages = JSON.parse(savedMessages)
      }
      savedMessages.push({ msg, direction, timestamp })
    }
  }

  sendAttachment(attachmentId, url) {
    let socketMsg = {
      action: 'onMessage',
      token: this.token,
      message: {
        type: 'Text',
        text: '',
        content: [
          {
            contentType: 'Attachment',
            attachment: {
              id: attachmentId,
            },
          },
        ],
      },
    }

    this.socket.send(JSON.stringify(socketMsg))
    const imgBox = this.$element.querySelector("input[type='file']")
    const fileMime = imgBox.files[0].type
    const isValidImage = this.imageExtensions.includes(fileMime)
    const fileName = imgBox.files[0].name
    const fileSize = imgBox.files[0].size

    imgBox.value = ''
    const preview = this.$element.querySelector('.cce-widget-chat__new-message__image')
    const filePreview = this.$element.querySelector('.cce-widget-chat__new-message__file')
    preview.classList.remove('cce-widget-chat__new-message__image__uploading')
    filePreview.classList.remove('cce-widget-chat__new-message__file__uploading')

    this.appendStartTime()
    const uniqueId = _.uniqueId()
    const attachment = {
      uniqueId: uniqueId,
      fileName: fileName,
      url: url,
      isValidImage: isValidImage,
      fileSize: fileSize,
      direction: 'self',
    }
    const content = this.createAttachmentContent(attachment)

    this.appendMessage(content, 'self', new Date().toISOString(), true, attachment)

    this.scrollMessages()
  }

  appendStartTime() {
    // TODO fix append start times
    return
    const storedMessages = localStorage.getItem('cce-genesys-messages')
    if (!storedMessages) {
      const currentTime = this.formatDateToDaysHours(new Date())
      const timestampStartDate = `<span id='timestampStartDate'>${currentTime}</span>`
      this.$element.querySelector('#cce-widget-chat__messages').insertAdjacentHTML('beforeend', timestampStartDate)
    }
  }

  sendTypingIndicator() {
    if (this.socket == null) {
      return
    }
    if (this.sendTypingIndicatorTimeout) {
      return
    }

    const socketMsg = {
      action: 'onMessage',
      token: this.token,
      message: {
        type: 'Event',
        events: [
          {
            eventType: 'Typing',
            typing: { type: 'On' },
          },
        ],
      },
    }
    this.socket.send(JSON.stringify(socketMsg))
    this.sendTypingIndicatorTimeout = setTimeout(() => {
      this.sendTypingIndicatorTimeout = null
    }, 5000)
  }

  flattenObject(obj, prefix = 'trueengage_form') {
    const result = {}

    function recurse(curr, property) {
      if (Object(curr) !== curr || curr === null) {
        result[property] = curr
      } else {
        for (let key in curr) {
          recurse(curr[key], `${property}_${key.replace(/\s+/g, '_')}`)
        }
      }
    }

    recurse(obj, prefix)
    return result
  }

  async sendMessage(textValue) {
    if (this.socket == null) {
      await this.initWebMessenger()
    }

    let maxRetries = 20
    let retryCount = 0
    while (this.socket && this.socket.readyState === WebSocket.CONNECTING && retryCount < maxRetries) {
      await new Promise((resolve) => setTimeout(resolve, 100))
      retryCount++
    }

    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.error('Socket is not open. Message not sent.')
      return
    }


    const msgBox = this.$element.querySelector('.cce-widget-chat__new-message textarea')
    let msg
    if (textValue) {
      msg = textValue
    } else {
      msg = msgBox.value
    }

    if (!isMessageContentValid(msg)) return
    this.triggerPublicEvent('message_sent', { communication_channel: 'chat' })

    //attachment
    const imgBox = this.$element.querySelector("input[type='file']")
    if (imgBox.value !== '') {
      const image = this.$element.querySelector('.cce-widget-chat__new-message__image')
      image.classList.add(`${this.styleNamespace}__attachment-loader`)
      this.requestPresignedUrl(imgBox.files[0])
    }

    if (msg.trim().length === 0) return

    try {
      const widgetId = this.widgetContainer.settings.widgets[this.widgetIndex].id
      cookieConsentManager.setLocalStorageItem(`trueengage-widget-opened-${widgetId}`, 'true', 'necessary')
    } catch (e) {
      console.error(e)
    }

    let socketMsg = {
      action: 'onMessage',
      token: this.token,
      message: {
        type: 'Text',
        text: msg,
        channel: {
          metadata: {
            customAttributes: {
              visitor_id: this.widgetContainer.token,
              te_channel: 'chat',
              ga_client_id: this.widgetContainer.googleAnalyticsClientId,

              //for upgrade do video
              te_deploymentId: this.params.deployment,
              te_token: this.token,
            },
          },
        },
      },
    }

    if (this.preInteractionFormEnabled && this.preInteractionFormExist) {
      const flattened = this.flattenObject(this.preInteractionFormData)
      Object.assign(socketMsg.message.channel.metadata.customAttributes, flattened)
    }

    let customAttributes = window.trueengage.get('customAttributes') || {}
    Object.assign(socketMsg.message.channel.metadata.customAttributes, customAttributes)

    msg = msg.replace(/\n/g, '<br>')
    this.appendStartTime()
    this.appendMessage(msg, 'self', new Date().toISOString())

    this.socket.send(JSON.stringify(socketMsg))
    msgBox.value = ''
    msgBox.parentElement.removeAttribute('good')
    msgBox.dispatchEvent(new Event('input'))
    this.scrollMessages()

    msgBox.style.height = ''
  }

  updateChannelAvatar(history, from, bot) {
    if (!history) {
      var avatarImg = document.getElementsByClassName('cce-widget_header--channel__chat-avatar-img')[0]

      var upgradeAvatarImg = document.querySelectorAll('.cce-widget-video__avatar')[0]

      if (upgradeAvatarImg) {
        var img = upgradeAvatarImg.querySelectorAll('img')[1]
        if (img) {
          img.src = from && from.image ? from.image : bot ? this.botimage : this.agentimage
        }
      }

      const popoverAvatarImg = document.querySelector('.cce-widget__call__popover__avatar img')
      if (avatarImg) {
        avatarImg.src = from && from.image ? from.image : bot ? this.botimage : this.agentimage
        popoverAvatarImg.src = from && from.image ? from.image : bot ? this.botimage : this.agentimage
      }
      var nickname = document.getElementsByClassName('cce-widget_header--channel__chat__data-name')[0]
      if (nickname) {
        nickname.innerHTML = `<div>${from && from.nickname ? from.nickname : ''}</div>`
      }
    }
  }

  updateChannelAvatarDirectly(avatar) {
    const avatarImg = document.getElementsByClassName('cce-widget_header--channel__chat-avatar-img')[0]
    const upgradeAvatarImg = document.querySelectorAll('.cce-widget-video__avatar')[0]
    const popoverAvatarImg = document.querySelector('.cce-widget__call__popover__avatar img')

    if (upgradeAvatarImg) {
      const img = upgradeAvatarImg.querySelectorAll('img')[1]
      if (avatar) {
        img.src = avatar ? avatar : this.agentimage
      }
    }
    if (avatarImg) {
      avatarImg.src = avatar ? avatar : this.agentimage
      popoverAvatarImg.src = avatar ? avatar : this.agentimage
    }
  }

  updateChannelNicknameDirectly(nickname = '') {
    const nicknameTag = document.getElementsByClassName('cce-widget_header--channel__chat__data-name')[0]
    if (nicknameTag) {
      nicknameTag.innerHTML = `<div>${nickname}</div>`
    }
  }

  resetChannelAvatar() {
    var avatarImg = document.getElementsByClassName('cce-widget_header--channel__chat-avatar-img')[0]
    if (avatarImg) {
      avatarImg.src = this.agentimage
    }
    var nickname = document.getElementsByClassName('cce-widget_header--channel__chat__data-name')[0]
    if (nickname) {
      nickname.innerHTML = ''
    }
  }

  //TODO if bot
  setBotAvatar(history, from) {
    if (!history) {
      var avatarImg = document.getElementsByClassName('cce-widget_header--channel__chat-avatar-img')[0]
      if (avatarImg) {
        avatarImg.src = this.botimage
      }
      var nickname = document.getElementsByClassName('cce-widget_header--channel__chat__data-name')[0]
      if (nickname) {
        nickname.innerHTML = `<div>${from && from.nickname ? from.nickname : ''}</div>`
      }
    }
  }

  getImgDownloadLink(url, fileName) {
    fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      })
  }

  createAttachmentContent(attachment) {
    const content = attachment.isValidImage
      ? `
            <div class="${this.styleNamespace}__attachment__image ${this.styleNamespace}__attachment-${attachment.uniqueId}">
              <div class="${this.styleNamespace}__attachment__image-${attachment.direction}">
                <span>${attachment.fileName}</span>
                <span>${(attachment.fileSize / 1000).toFixed(1)} KB</span>
              </div>
              <img class="${this.styleNamespace}__attachment-img ${this.styleNamespace}__attachment-${attachment.uniqueId}" src="${attachment.url}" alt=""/>
            </div>
          `
      : `
            <div class="${this.styleNamespace}__attachment__file ${this.styleNamespace}__attachment-${attachment.uniqueId}">
              <svg class="${this.styleNamespace}__attachment-svg" width="24" height="24" viewBox="0 0 24 24" style="width: 24px;height: 24px;cursor: pointer"><path fill="currentColor" d="M4.616 22q-.691 0-1.153-.462T3 20.385V8h1v12.385q0 .23.192.423t.423.192H14v1zm4-4q-.691 0-1.153-.462T7 16.384V3.616q0-.691.463-1.153T8.616 2H15.5L20 6.5v9.885q0 .69-.462 1.153T18.384 18zM15 7h4l-4-4z"/></svg>
              <div class="${this.styleNamespace}__attachment__file-${attachment.direction}">
                <span>${attachment.fileName}</span>
                <span>${(attachment.fileSize / 1000).toFixed(1)} KB</span>
              </div>
            </div>
          `

    return content
  }

  receiveMessage(msg, history = false) {
    const notificationDivs = document.querySelectorAll('.chat__notification')
    const chatDiv = document.querySelector('.cce-widget-chat')
    const widgetWindow = document.querySelector('.cce-widget__window')
    if (msg.body && msg.body.text) {
      msg.body.text = msg.body.text.replace(/\n/g, '<br/>')
    }
    if (chatDiv.classList.contains('open') && widgetWindow.classList.contains('cce-widget__window--open')) {
      if (localStorage.getItem('trueengage-widget-minimized') === 'false') {
        cookieConsentManager.setLocalStorageItem('lastChatInteraction', new Date(), 'necessary')
      }
    }

    if (msg.body.direction == 'Outbound') {
      if (!history && msg.body && msg.body.type !== 'Event') {
        if (this.widgetContainer.isNewestTab() && !this.isActive) {
          this.widgetContainer.playNotificationSound()
          const widgetContainer = document.querySelector('.cce-widget-container')
          if (
            !chatDiv.classList.contains('open') ||
            (chatDiv.classList.contains('open') && !widgetWindow.classList.contains('cce-widget__window--open')) ||
            widgetContainer.classList.contains('minimized')
          ) {
            notificationDivs.forEach((notificationDiv) => {
              let currentNumber = parseInt(notificationDiv.textContent) || 0
              currentNumber++
              notificationDiv.textContent = currentNumber
              notificationDiv.classList.add('chat__notification-show')
            })
          }
        }
      }

      if (msg.body.content && msg.body.content.some((x) => x.contentType === 'Carousel')) {
        let cards = msg.body.content.find((x) => x.contentType === 'Carousel').carousel.cards
        this.appendCarousel(cards, 'remote')
      }

      if (_.isString(msg.body.text)) {
        if (msg.body.channel.from) {
          if (msg.body.originatingEntity == 'Bot') {
            if (msg.body.channel.from) {
              this.appendMessage(msg.body.text, 'remote', msg.body.channel.time, msg.body.channel.from.image ? msg.body.channel.from.image : this.botimage)
              this.updateChannelNicknameDirectly(msg.body.channel.from.nickname ? msg.body.channel.from.nickname : '')
            } else {
              this.appendMessage(msg.body.text, 'remote', msg.body.channel.time, this.botimage)
              this.updateChannelNicknameDirectly('Bot')
            }
          } else {
            this.appendMessage(msg.body.text, 'remote', msg.body.channel.time, msg.body.channel.from.image)
            this.updateChannelNicknameDirectly(msg.body.channel.from.nickname ? msg.body.channel.from.nickname : '')
          }
        } else {
          this.appendMessage(msg.body.text, 'remote', msg.body.channel.time)
          this.updateChannelNicknameDirectly(msg.body.channel.from.nickname ? msg.body.channel.from.nickname : '')
          this.resetChannelAvatar()
        }

      } else if (
        msg.body.content !== null &&
        msg.body.content !== undefined &&
        ((Array.isArray(msg.body.content) &&
          msg.body.content.length > 0 &&
          msg.body.content[0].contentType != null &&
          msg.body.content[0].contentType !== 'Carousel') ||
          (!Array.isArray(msg.body.content) && msg.body.content.contentType != null && msg.body.content.contentType !== 'Carousel'))
      ) {
        msg.body.content.forEach((c) => {
          const uniqueId = _.uniqueId()
          const fileMime = c.attachment.mime
          const fileName = c.attachment.filename
          const fileSize = c.attachment.fileSize
          const isValidImage = this.imageExtensions.includes(fileMime)
          let url = c.attachment.url
          const attachment = {
            uniqueId: uniqueId,
            fileName: fileName,
            url: url,
            isValidImage: isValidImage,
            fileSize: fileSize,
            direction: 'remote',
          }
          const content = this.createAttachmentContent(attachment)

          this.appendMessage(content, 'remote', msg.body.channel.time, msg.body.channel.from.image, attachment)
        })
      }

      //typing
      if (msg.body.events && msg.body.events.some((x) => x.eventType === 'Typing')) {
        var event = msg.body.events.find((x) => x.eventType === 'Typing')
        if (event.typing.type === 'On') {
          this.showTypingIndicator()
        }
      }

      //interaction forms
      if (msg.body.text && msg.body.text.startsWith('TE_OPEN_FORM_') && !history) {
        //this.interactionForms
        const match = msg.body.text.match(/^TE_OPEN_FORM_([0-9a-fA-F-]{36})$/)
        const formId = match ? match[1] : null
        if (formId) {
          this.showInteractionForm(formId)
        } else {
          console.warn('Nieprawidłowy format wiadomości:', text)
        }
      }

      //history interaction forms
      if (msg.body.text && msg.body.text.startsWith('TE_OPEN_FORM_') && history) {
        //do nothing
      }

      //videoUpgrade
      if (msg.body.text === '[TE_VIDEO_OFFER]') {
        this.appendUpgradeRequest('VideoOffer')
      } else if (msg.body.text === '[TE_AUDIO_OFFER]') {
        this.appendUpgradeRequest('AudioOffer')
      } else {
        //co-browse
        if (msg.body.events && msg.body.events.some((x) => x.eventType === 'CoBrowse')) {
          var coBrowseSettings = msg.body.events.find((x) => x.eventType == 'CoBrowse')
          if (msg.body.channel.from) {
            this.appendCoBrowseRequest(
              coBrowseSettings.coBrowse.sessionId,
              coBrowseSettings.coBrowse.sessionJoinToken,
              coBrowseSettings.coBrowse.type,
              'remote',
              msg.body.channel.time,
              history,
              msg.body.channel.from.image
            )
            this.updateChannelAvatar(history, msg.body.channel.from, false)
          } else {
            this.appendCoBrowseRequest(
              coBrowseSettings.coBrowse.sessionId,
              coBrowseSettings.coBrowse.sessionJoinToken,
              coBrowseSettings.coBrowse.type,
              'remote',
              msg.body.channel.time,
              history,
              this.agentimage
            )
          }
        }

        //quick replies
        if (!history && msg.body.content && msg.body.content.some((x) => x.contentType === 'QuickReply')) {
          let options = []
          msg.body.content.forEach((qr) => {
            if (qr.contentType === 'QuickReply' && _.isObject(qr.quickReply)) {
              let payload = qr.quickReply.payload
              let text = qr.quickReply.text
              let action = qr.quickReply.action

              options.push({ text, payload, action })
            }
          })
          if (options.length != 1 && options[0].text !== 'TE_FORM_FILLED') {
            if (this.headsUpType === 'quickReplies') {
              this.appendQuickReplyOptions(options, 'remote', msg.body.channel.time, true, true, msg.body.text)
            } else {
              this.appendQuickReplyOptions(options, 'remote', msg.body.channel.time)
            }
          }
        }

        //Presence status
        if (msg.body.type === 'Event' && msg.body.events.some((x) => x.eventType === 'Presence')) {
          var event = msg.body.events.find((x) => x.eventType === 'Presence')
          if (event.presence.type === 'Disconnect') {
            if (this.requestId !== '') {
              this.rejectVideoOffer(this.requestId)
            }
            this.appendDisconnectStatusInfo(msg.body.channel.time)
          }
        }
      }
      document.querySelectorAll(`.${this.styleNamespace}__attachment-img`).forEach((elem) => {
        elem.addEventListener('load', () => {
          this.scrollMessages()
        })
      })

      this.scrollMessages()
    } else {
      //message from the user
      if (!document.hasFocus()) {
        const msgBody = msg.body
        if (msg.type === 'message' && msgBody.type === 'Text' && _.isString(msgBody.text)) {
          if (msgBody.text === 'TE_VIDEO_REQUEST_REJECTED') {
            return
          }
          this.appendMessage(msgBody.text, 'self', msgBody.channel.time)
        } else if (msgBody.content !== null && msgBody.content !== undefined) {
          msgBody.content.forEach((c) => {
            if (c.contentType === 'ButtonResponse') {
              let text = c.buttonResponse.text
              this.appendMessage(text, 'self', msgBody.channel.time)
            } else {
              let imgUrl = c.attachment.url
              this.appendMessage(
                `<img onclick='window.open("${imgUrl}");' class='${this.styleNamespace}__attachment-img' src='${imgUrl}' />`,
                'self',
                msgBody.channel.time
              )
            }
          })
        }
        this.scrollMessages()
      }

      if (msg.body.type == 'Event') {
        //co-browse
        if (msg.body.events && msg.body.events.some((x) => x.eventType === 'CoBrowse')) {
          var coBrowseSettings = msg.body.events.find((x) => x.eventType == 'CoBrowse')
          this.appendCoBrowseRequest(
            coBrowseSettings.coBrowse.sessionId,
            coBrowseSettings.coBrowse.sessionJoinToken,
            coBrowseSettings.coBrowse.type,
            'remote',
            msg.body.channel.time,
            history
          )
        }
      } else if (!this.billed && msg.body.channel && msg.body.channel.messageId && msg.type === 'message') {
        this.triggerPublicEvent('chat_started', { communication_channel: 'chat', visitor_id: this.widgetContainer.token, message_id: msg.body.id })
        this.triggerPublicEvent('action_chat_start', { channel: 'chat' })
        this.widgetContainer.triggerPublicEvent('widget_business_success', { state: widgetManager.getState(), confirmed: true })

        this.billingService.addInteraction({
          interactionType: 'Chat',
          additionalInfo: {
            messageId: msg.body.id,
            visitorId: this.widgetContainer.token,
          },
        })
        this.billed = true
        this.firstMessageId = msg.body.id
      }
    }
  }

  showInteractionForm(formId) {
    const selectedForm = this.interactionForms.find((form) => form.id === formId)

    selectedForm.fields = selectedForm.schema

    console.log(selectedForm)

    this.$element.insertAdjacentHTML(
      'beforeend',
      templates.preInteractionFormTemplate({
        form: selectedForm,
        styleNamespace: this.styleNamespace,
        submitButtonText: this.texts.submitInInteractionFormText,
        texts: this.preInteractionFormTexts,
        additionalClassname: '',
        formDataType: 'chat',
        optionalHeaderText: selectedForm.texts.headerText,
      })
    )

    this.$element.querySelector('.cce-widget-chat__messages').style.display = 'none'
    this.$element.querySelector('.cce-widget-chat__form').style.display = 'none'

    const headerContainer = document.querySelector('.cce-widget__window__header')

    if (headerContainer) {
      headerContainer.classList.add('cce-widget__window--with-in-interaction-form')
    }

    const formContainer = this.$element.querySelector(`.${this.styleNamespace}__pre-interaction-form`)
    formContainer.style.display = 'flex'
    formContainer.classList.add('in-interaction-form')
    this.$element.querySelector('.cce-widget__pre-interaction-form__footer').style.display = 'flex'
    this.adjustPreInteractionFormConsentsStyles('chat')
    const topicLabels = formContainer.querySelectorAll('[class*="cce-widget__pre-interaction-form-topicList"]')

    topicLabels.forEach((topicLabel) => {
      const topicInput = topicLabel.querySelector('input')
      const topicList = topicLabel.querySelector('ul')

      if (!topicInput || !topicList) return

      topicInput.addEventListener('click', () => {
        topicList.classList.toggle('show')
      })

      const topicItems = topicList.querySelectorAll('li')

      topicItems.forEach((item) => {
        item.addEventListener('click', (e) => {
          e.preventDefault()
          topicInput.value = item.textContent.trim()

          topicItems.forEach((i) => i.classList.remove('selected'))
          item.classList.add('selected')

          topicList.classList.remove('show')
          topicLabel.classList.remove('error')
        })
      })

      if (topicItems.length > 0) {
        topicItems[0].click()
      }
    })

    this.$element.querySelector(`.cce-widget-chat__pre-interaction-form`).addEventListener('submit', (event) => {
      event.preventDefault()
      event.stopPropagation()
      let formData = new FormData(event.target)
      let formValues = Object.fromEntries(formData.entries())
      if (!this.validatePreInteractionForm('chat', this.preInteractionFormIti.form, this.preInteractionFormTexts)) return
      Object.keys(formValues).forEach((key) => {
        if (key.startsWith('consents[')) {
          delete formValues[key]
        }
      })

      event.target.querySelectorAll("input[type='checkbox']").forEach((checkbox) => {
        let match = checkbox.name.match(/^consents\[(.+)\]$/)
        if (match) {
          if (!formValues.consents) formValues.consents = {}
          formValues.consents[match[1]] = checkbox.checked
        }
      })

      if (this.preInteractionFormIti.form) {
        formValues.phoneNumber = this.preInteractionFormIti.form.getNumber()
      }

      this.preInteractionFormData = formValues
      const stringValue = JSON.stringify(formValues)
      cookieConsentManager.setLocalStorageItem('cce-widget-chat-form-data', stringValue, 'necessary')

      this.preInteractionFormExist = true

      const chatContainer = document.querySelector('.cce-widget-chat')
      const headerContainer = document.querySelector('.cce-widget__window__header')

      if (chatContainer) {
        chatContainer.classList.remove('formOpen')
        chatContainer.classList.add('open')
        headerContainer.classList.remove('cce-widget__window--with-in-interaction-form')
      }

      this.sendFormMessage('TE_FORM_FILLED', formValues)
      formContainer.remove()

      this.$element.querySelector('.cce-widget-chat__messages').style.display = 'flex'
      this.$element.querySelector('.cce-widget-chat__form').style.display = 'flex'
    })
  }

  showTypingIndicator() {
    let typingIndicator = this.$element.querySelector('#cce-widget-chat__msg-typing-indicator')

    if (typingIndicator) {
      console.log('clearTimeout')
      clearTimeout(this.typingIndicatorTimeout)
    } else {
      let messageTemplate = templates.chatMessageTypingIndicatorTemplate({
        message: '...',
        direction: 'remote',
        styleNamespace: this.styleNamespace,
      })

      this.$element.querySelector('#cce-widget-chat__messages').insertAdjacentHTML('beforeend', messageTemplate)

      this.scrollMessages()
    }

    this.typingIndicatorTimeout = setTimeout(() => {
      const typingIndicator = this.$element.querySelector('#cce-widget-chat__msg-typing-indicator')
      if (typingIndicator) {
        typingIndicator.remove()
      }
    }, 6 * 1000)
  }

  scrollMessages() {
    setTimeout(() => {
      let elem = this.$element.querySelector('#' + this.styleNamespace + '__messages')
      if (elem) {
        elem.scrollTop = elem.scrollHeight
      }
    })

    document.querySelectorAll(`.${this.styleNamespace}__attachment-img`).forEach((elem) => {
      elem.addEventListener('load', () => {
        this.scrollMessages()
      })
    })
  }

  sendPingMessage() {
    const pingMessage = {
      action: 'echo',
      message: {
        type: 'Text',
        text: 'ping',
      },
    }
    this.socket.send(JSON.stringify(pingMessage))
  }

  async getToken() {
    const widgetId = this.widgetContainer.settings.widgets[this.widgetIndex].id
    const visitorId = this.widgetContainer.token

    const key = `trueengage-genesys-web-messenger-token-${widgetId}-${visitorId}`
    let token = window.localStorage.getItem(key)
    if (token === null) {
      token = await this.tokenService.getToken(visitorId)
    }

    if (token === null || token === 'Visitor not found!') {
      token = this.generateUUID()
      cookieConsentManager.setLocalStorageItem(key, token, 'necessary')
      await this.tokenService.saveToken(visitorId, token)
    }
    return token
  }

  getHistoryToken() {
    const msg = {
      action: 'getJwt',
      token: this.token,
    }
    this.socket.send(JSON.stringify(msg))
  }

  fillHistoryFromCache() {
    let savedMessages = localStorage.getItem('cce-genesys-messages')
    if (savedMessages === null) {
      return
    } else {
      savedMessages = JSON.parse(savedMessages)
    }

    savedMessages.forEach((m) => {
      this.appendMessage(m.msg, m.direction, m.timestamp, false)
    })
    this.scrollMessages()
  }

  findFirstQuickReplyAfterUserInteraction(entities, lastChatInteraction) {
    for (const msg of entities) {
      if (new Date(msg.channel.time) > new Date(lastChatInteraction)) {
        if (msg.direction === 'Outbound' && msg.originatingEntity === 'Bot' && msg.type === 'Structured') {
          return msg
        }
      }
    }
    return null
  }

  getConversationHistory() {
    localStorage.removeItem('cce-genesys-messages')
    const lastChatInteraction = localStorage.getItem('lastChatInteraction')
    let ignoreMessageId = null
    this.setChatState('loading')

    this.genesysWebMessengerHistoryService.getHistory({ jwtToken: this.historyToken }, (response) => {
      response
        .json()
        .then((data) => {
          this.setChatState()
          if (_.isObject(data.entities)) {
            let entities = data.entities.sort(function (a, b) {
              if (new Date(a.channel.time) < new Date(b.channel.time)) return -1
              if (new Date(b.channel.time) < new Date(a.channel.time)) return -1
              return 0
            })
            let notificationCount = 0
            if (this.headsUpType === 'quickReplies') {
              const firstQuickReplyAfterUserInteraction = this.findFirstQuickReplyAfterUserInteraction(entities, lastChatInteraction)
              if (firstQuickReplyAfterUserInteraction) {
                ignoreMessageId = firstQuickReplyAfterUserInteraction.id
                try {
                  this.receiveMessage({ body: firstQuickReplyAfterUserInteraction }, true)
                } catch (error) {
                  console.error('Error in receiveMessage:', error)
                }

                try {
                  this.appendQuickReplyOptionsFromHistory(firstQuickReplyAfterUserInteraction)
                } catch (error) {
                  console.error('Error in appendQuickReplyOptionsFromHistory:', error)
                }
              }
            }
            entities.forEach((msg) => {
              if (new Date(msg.channel.time) > new Date(lastChatInteraction)) {
                if ((msg.type == 'Event' && msg.events.some((x) => x.eventType == 'Presence')) || msg.direction === 'Inbound') {
                  //ignore
                } else {
                  notificationCount += 1
                }
              }
              if (msg.direction == 'Outbound') {
                if (ignoreMessageId !== msg.id) this.receiveMessage({ body: msg }, true)
              } else {
                if (_.isString(msg.text)) {
                  msg.text = msg.text.replace(/\n/g, '<br/>')
                  this.appendMessage(msg.text, 'self', msg.channel.time)
                } else if (msg.content !== null && msg.content !== undefined) {
                  msg.content.forEach((c) => {
                    let imgUrl = c.attachment.url
                    const fileName = c.attachment.filename
                    const fileSize = c.attachment.fileSize
                    const isValidImage = this.imageExtensions.includes(c.attachment.mime)
                    const uniqueId = _.uniqueId()
                    const attachment = {
                      uniqueId: uniqueId,
                      fileName: fileName,
                      url: imgUrl,
                      isValidImage: isValidImage,
                      fileSize: fileSize,
                      direction: 'self',
                    }
                    const content = this.createAttachmentContent(attachment)

                    this.appendMessage(content, 'self', msg.channel.time, true, attachment)
                  })
                } else if (msg.type == 'Event' && msg.events.some((x) => x.eventType == 'Presence')) {
                  //ignore
                } else if (msg.type == 'Event') {
                  var coBrowseSettings = msg.events.find((x) => x.eventType == 'CoBrowse')
                  this.appendCoBrowseRequest(
                    coBrowseSettings.coBrowse.sessionId,
                    coBrowseSettings.coBrowse.sessionJoinToken,
                    coBrowseSettings.coBrowse.type,
                    'remote',
                    msg.channel.time,
                    history
                  )
                }
              }
            })
            const notificationDivs = document.querySelectorAll('.chat__notification')
            notificationDivs.forEach((notificationDiv) => {
              if (notificationCount === 0) return
              notificationDiv.textContent = notificationCount.toString()
              notificationDiv.classList.add('chat__notification-show')
            })
            this.scrollMessages()
          }
        })
        .catch((_) => {
          this.setChatState()
        })
    })
  }

  setChatState(state = '') {
    const chatWindow = this.$element

    chatWindow.classList.remove('loading')

    if (state !== '') {
      chatWindow.classList.add(state)
    }
  }

  formatDate(date) {
    let d = new Date(date)
    let month = (d.getMonth() + 1).toString().padStart(2, 0)
    let day = d.getDate().toString().padStart(2, 0)
    let year = d.getFullYear()

    var hours = d.getHours().toString().padStart(2, 0)
    var minutes = d.getMinutes().toString().padStart(2, 0)
    var seconds = d.getSeconds().toString().padStart(2, 0)

    var dateString = [hours, minutes, seconds].join(':')

    var currentDate = new Date()
    if (!(d.getDate() == currentDate.getDate() && d.getMonth() == currentDate.getMonth() && d.getFullYear() == currentDate.getFullYear())) {
      dateString = [year, month, day].join('-') + ' ' + dateString
    }

    return dateString
  }

  requestPresignedUrl(file) {
    const msg = {
      action: 'onAttachment',
      token: this.token,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
    }

    const preview = this.$element.querySelector('.cce-widget-chat__new-message__image')
    const filePreview = this.$element.querySelector('.cce-widget-chat__new-message__file')
    preview.classList.add('cce-widget-chat__new-message__image__uploading')
    filePreview.classList.add('cce-widget-chat__new-message__file__uploading')

    this.socket.send(JSON.stringify(msg))
  }

  initCoBrowse(depylomentId, environment, regionShort) {
    ;(function (g, e, n, es, ys) {
      g['_genesysJs'] = e
      g[e] =
        g[e] ||
        function () {
          ;(g[e].q = g[e].q || []).push(arguments)
        }
      g[e].t = 1 * new Date()
      g[e].c = es
      ys = document.createElement('script')
      ys.async = 1
      ys.src = n
      ys.charset = 'utf-8'
      document.head.appendChild(ys)
    })(window, 'Genesys', `https://apps.${environment}/genesys-bootstrap/genesys.min.js`, {
      environment: regionShort,
      deploymentId: depylomentId,
    })
    cookieConsentManager.setLocalStorageItem('cce-widget-chat-deploymentId', this.deploymentId, 'necessary')

    Genesys('subscribe', 'CobrowseService.sessionEnded', () => {
      this.removeCoBrowseInfoPanel()
      cookieConsentManager.setLocalStorageItem('cce-genesys-active-cobrowse', null, 'necessary')
    })

    Genesys('subscribe', 'CobrowseService.controlRequested', () => {
      this.showCoBrowseControlRequestInfo()
    })

    Genesys('subscribe', 'CobrowseService.controlRevoked', () => {
      this.showCoBrowseInfo()
    })

    Genesys('subscribe', 'CobrowseService.controlGranted', () => {
      this.showCoBrowseControlGrantedInfo()
    })
  }

  runAfterTrigger(quickReplies) {
    if (!this.preInteractionFormEnabled || this.preInteractionFormExist) {
      this.sendWelcomeMessage()
    }
    if (this.usingPredictiveEngagement && quickReplies && !this.headsUpQuickRepliesOpened) {
      this.quickReplies.open()
      return
    }
    this.triggers.forEach((trigger) => {
      if (trigger.component !== 'popover') return
      if (trigger.kind === 'load') {
        if (quickReplies && !this.headsUpQuickRepliesOpened) {
          this.headsUpQuickRepliesOpened = true
          const isWidgetOpen = document.querySelector('.cce-widget__window').classList.contains('cce-widget__window--open')
          if (isWidgetOpen) return
          this.quickReplies.open()
        }
      }

      if (trigger.kind === 'totalTimeSpent') {
        let timeSpentOnWidget = parseInt(this.container.visitor.timesOnWidgets[this.settings.id]) || 0
        let delay = Math.max(0, parseInt(trigger.value) - timeSpentOnWidget)
        setTimeout(
          () => {
            if (quickReplies && !this.headsUpQuickRepliesOpened) {
              this.headsUpQuickRepliesOpened = true
              const isWidgetOpen = document.querySelector('.cce-widget__window').classList.contains('cce-widget__window--open')
              if (isWidgetOpen) return
              this.quickReplies.open()
            }
          },
          Math.max((delay - 3) * 1000, 0)
        )
      }

      if (trigger.kind === 'timeSpent') {
        setTimeout(
          () => {
            if (quickReplies && !this.headsUpQuickRepliesOpened) {
              this.headsUpQuickRepliesOpened = true
              const isWidgetOpen = document.querySelector('.cce-widget__window').classList.contains('cce-widget__window--open')
              if (isWidgetOpen) return
              this.quickReplies.open()
            }
          },
          parseInt(trigger.value) * 1000
        )
      }

      if (trigger.kind === 'scrollPercentage') {
        window.addEventListener('scroll', () => {
          var scrollTop = window.scrollY
          var docHeight = document.body.scrollHeight
          var winHeight = window.innerHeight
          var scrollPercentage = Math.round((scrollTop / (docHeight - winHeight)) * 100)
          if (parseFloat(trigger.value) <= scrollPercentage) {
            if (quickReplies && !this.headsUpQuickRepliesOpened) {
              this.headsUpQuickRepliesOpened = true
              const isWidgetOpen = document.querySelector('.cce-widget__window').classList.contains('cce-widget__window--open')
              if (isWidgetOpen) return
              this.quickReplies.open()
            }
          }
        })
      }

      if (trigger.kind === 'exitIntent') {
        window.addEventListener(
          'blur',
          (e) => {
            let from
            from = e.relatedTarget || e.toElement
            if (from || e.clientY > 100) {
              return
            }
            if (quickReplies && !this.headsUpQuickRepliesOpened) {
              this.headsUpQuickRepliesOpened = true
              const isWidgetOpen = document.querySelector('.cce-widget__window').classList.contains('cce-widget__window--open')
              if (isWidgetOpen) return
              this.quickReplies.open()
            }
          },
          false
        )
      }
    })
  }

  sendFormMessage(msg, formValues) {
    let socketMsg = {
      action: 'onMessage',
      token: this.token,
      message: {
        type: 'Text',
        text: msg,
        channel: {
          metadata: {
            customAttributes: {
              visitor_id: this.widgetContainer.token,
              te_channel: 'chat',
              ga_client_id: this.widgetContainer.googleAnalyticsClientId,

              //for upgrade do video
              te_deploymentId: this.params.deployment,
              te_token: this.token,
            },
          },
        },
      },
    }
    if (formValues) {
      const flattened = this.flattenObject(formValues)
      Object.assign(socketMsg.message.channel.metadata.customAttributes, flattened)
    }
    let customAttributes = window.trueengage.get('customAttributes') || {}
    Object.assign(socketMsg.message.channel.metadata.customAttributes, customAttributes)

    this.socket.send(JSON.stringify(socketMsg))
  }

  sendWelcomeMessage(formValues) {
    const customerJoinMessage = {
      action: 'onMessage',
      message: {
        type: 'Event',
        events: [
          {
            eventType: 'Presence',
            presence: {
              type: 'Join',
            },
          },
        ],
        channel: {
          metadata: {
            customAttributes: {
              visitor_id: this.widgetContainer.token,
              te_channel: 'chat',
              ga_client_id: this.widgetContainer.googleAnalyticsClientId,
            },
          },
        },
      },
      token: this.token,
    }
    if (formValues) {
      const flattened = this.flattenObject(formValues)
      Object.assign(customerJoinMessage.message.channel.metadata.customAttributes, flattened)
    }
    let customAttributes = window.trueengage.get('customAttributes') || {}
    Object.assign(customerJoinMessage.message.channel.metadata.customAttributes, customAttributes)

    this.socket.send(JSON.stringify(customerJoinMessage))
  }

  async initWebMessenger() {
    if (!this.messengerInitialized) {
      this.socket = new WebSocket(`${this.websocketUrl}/v1?deploymentId=${this.deploymentId}`)
      this.token = await this.getToken()

      const createHubConnection = async () => {
        this.hubConnection = new signalR.HubConnectionBuilder()
          .withUrl(config.interactionHub.host, { withCredentials: false })
          .configureLogging(signalR.LogLevel.Information)
          .build()

        try {
          await this.hubConnection.start({ withCredentials: false })
        } catch (error) {
          return
        }
        await this.hubConnection.invoke('JoinInteractionGroup', this.widgetContainer.settings.id, this.widgetContainer.token)
        this.hubConnection.on('ReceiveInvitation', (accountId, visitorId, invitation) => {
          if (this.widgetContainer.settings.id === accountId && this.widgetContainer.token === visitorId) {
            this.appendUpgradeRequest(invitation)
          }
          const notificationDivs = document.querySelectorAll('.chat__notification')
          const widgetWindow = document.querySelector('.cce-widget__window')
          const chatDiv = document.querySelector('.cce-widget-chat')
          if (!chatDiv.classList.contains('open') || (chatDiv.classList.contains('open') && !widgetWindow.classList.contains('cce-widget__window--open'))) {
            this.widgetContainer.playNotificationSound()
            notificationDivs.forEach((notificationDiv) => {
              let currentNumber = parseInt(notificationDiv.textContent) || 0
              currentNumber++
              notificationDiv.textContent = currentNumber
              notificationDiv.classList.add('chat__notification-show')
            })
          }
        })
      }
      createHubConnection()

      const initMsg = {
        action: 'configureSession',
        deploymentId: this.deploymentId,
        token: this.token,
      }

      let _this = this

      function websocketOpened() {
        _this.socket.send(JSON.stringify(initMsg))
        _this.messengerInitialized = true
      }

      if (this.socket.readyState === WebSocket.OPEN) {
        websocketOpened()
      } else {
        this.socket.addEventListener('open', function () {
          websocketOpened()
        })
      }

      this.socket.addEventListener('message', function (event) {
        let parsedResponse = JSON.parse(event.data)
        if (parsedResponse.type == 'response' && parsedResponse.body.connected) {
          const fileInput = document.querySelector('#cce-widget-chat__attachment')
          const fileConfig = parsedResponse.body.allowedMedia.inbound
          const allowedFileTypes = fileConfig.fileTypes.map((fileType) => fileType.type)

          if (allowedFileTypes.length < 1) {
            document.querySelector('.cce-widget-chat__btn-attach').remove()

            fileInput.disabled = true
          }

          if (fileInput) {
            fileInput.setAttribute('accept', allowedFileTypes.join(','))
          }

          setInterval(() => _this.sendPingMessage(), 50000)

          if (!parsedResponse.body.newSession) {
            _this.getHistoryToken()
          } else {
            //New Session
            //automatically send initial message
            if (_this.autoStartEnabled && _this.headsUpType !== 'quickReplies') {
              if (!_this.preInteractionFormEnabled || _this.preInteractionFormExist) {
                _this.sendWelcomeMessage()
              }
            } else {
              _this.runAfterTrigger()
            }

            if (_this.params.welcomeText) {
              _this.appendMessage(_this.params.welcomeText, 'remote', new Date().toISOString())
            }
          }
          _this.sendMessage()
        } else if (parsedResponse.type === 'response' && parsedResponse.class === 'JwtResponse') {
          _this.historyToken = parsedResponse.body.jwt
          _this.getConversationHistory()
        } else if (parsedResponse.type === 'response' && parsedResponse.class === 'PresignedUrlResponse') {
          //upload attachment to the provided url
          let parsedHeaders = parsedResponse.body.headers

          const formData = new FormData()
          const fileField = _this.$element.querySelector('input[type="file"]')

          formData.append('file', fileField.files[0])

          fetch(parsedResponse.body.url, {
            method: 'PUT',
            body: fileField.files[0],
            headers: parsedHeaders,
          })
            .then(() => {})
            .catch(() => {})
        } else if (parsedResponse.type === 'message' && parsedResponse.class === 'UploadSuccessEvent') {
          //send message with attachment
          _this.sendAttachment(parsedResponse.body.attachmentId, parsedResponse.body.downloadUrl)
          const image = _this.$element.querySelector('.cce-widget-chat__new-message__image')
          image.classList.add('cce-widget-chat__new-message__image--hidden')
          image.classList.remove('cce-widget-chat__attachment-loader')
          const preview = _this.$element.querySelector('.cce-widget-chat__new-message__image-wrap')
          preview.classList.add('cce-widget-chat__new-message__image--hidden')

          const file = _this.$element.querySelector('.cce-widget-chat__new-message__file')
          file.classList.add('cce-widget-chat__new-message__file--hidden')
          file.classList.remove('cce-widget-chat__attachment-loader')
          const filePreview = _this.$element.querySelector('.cce-widget-chat__new-message__file-wrap')
          filePreview.classList.add('cce-widget-chat__new-message__file--hidden')

          //append message
        } else {
          let message = new GenesysChatMessage(parsedResponse)
          _this.receiveMessage(message)
        }
      })
    } else {
      this.fillHistoryFromCache()
    }
  }

  handleFormSubmit() {
    this.sendMessage()
  }

  clearConversation() {
    if (this.socket == null) {
      return
    }

    const socketMsg = {
      action: 'onMessage',
      message: {
        type: 'Event',
        events: [
          {
            eventType: 'Presence',
            presence: {
              type: 'Clear',
            },
          },
        ],
      },
      token: this.token,
    }
    this.socket.send(JSON.stringify(socketMsg))
    this.$element.querySelector(`.${this.styleNamespace}__messages`).innerHTML = ''
    this.messengerInitialized = false
    this.socket = null
    const widgetId = this.widgetContainer.settings.widgets[this.widgetIndex].id
    localStorage.removeItem(`trueengage-activeChannel-${widgetId}-${this.widgetContainer.token}`)
  }

  observeEvents() {
    this.$element.querySelector('.cce-widget-chat__form').addEventListener('submit', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.handleFormSubmit()
    })

    let _this = this

    function updateChatActivityStatus(e) {
      setTimeout(() => {
        this.isActive = hasParentWithClass(document.activeElement, _this.styleNamespace)
        if (this.isActive) e.stopPropagation()
      }, 0)
    }

    function hasParentWithClass(element, className) {
      while (element) {
        if (element.classList && element.classList.contains(className)) {
          return true
        }
        element = element.parentElement
      }
      return false
    }

    document.addEventListener('focusin', updateChatActivityStatus.bind(this))
    document.addEventListener('focusout', () => {
      if (!document.hasFocus()) {
        this.isActive = false
      } else {
        updateChatActivityStatus.bind(this)
      }
    })
    window.addEventListener('focus', updateChatActivityStatus.bind(this))

    this.$element.querySelector('input[type=file]').addEventListener('change', () => {
      const loader = this.$element.querySelector(`.${this.styleNamespace}__new-message__image--loader`)
      loader.classList.remove(`${this.styleNamespace}__new-message__image--hidden`)

      const file = this.$element.querySelector('#cce-widget-chat__attachment').files[0]
      if (file) {
        const reader = new FileReader()
        const preview = this.$element.querySelector('.cce-widget-chat__new-message__image')
        const filePreview = this.$element.querySelector('.cce-widget-chat__new-message__file')
        const previewContainer = this.$element.querySelector('.cce-widget-chat__new-message__image-wrap')
        const filePreviewContainer = this.$element.querySelector('.cce-widget-chat__new-message__file-wrap')

        reader.addEventListener(
          'load',
          () => {
            if (this.imageExtensions.includes(file.type)) {
              preview.src = reader.result
              preview.classList.remove('cce-widget-chat__new-message__image--hidden')
              previewContainer.classList.remove('cce-widget-chat__new-message__image--hidden')
              filePreviewContainer.classList.add(`${this.styleNamespace}__new-message__file--hidden`)
            } else {
              filePreview.classList.remove('cce-widget-chat__new-message__file--hidden')
              filePreviewContainer.classList.remove('cce-widget-chat__new-message__file--hidden')
              previewContainer.classList.add(`${this.styleNamespace}__new-message__image--hidden`)
            }
            loader.classList.add(`${this.styleNamespace}__new-message__image--hidden`)
          },
          false
        )

        reader.readAsDataURL(file)
        this.$element.querySelector('.cce-widget-chat__new-message textarea').focus()
      }
    })

    this.$element.querySelector('.cce-widget-chat__btn-attach').addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.$element.querySelector('input[type=file]').click()
    })

    const previewContainer = this.$element.querySelector('.cce-widget-chat__new-message__image-wrap')
    previewContainer.addEventListener('click', () => {
      this.$element.querySelector('input[type=file]').value = ''
      previewContainer.classList.add(`${this.styleNamespace}__new-message__image--hidden`)
    })

    const filePreviewContainer = this.$element.querySelector('.cce-widget-chat__new-message__file-wrap')
    filePreviewContainer.addEventListener('click', () => {
      this.$element.querySelector('input[type=file]').value = ''
      filePreviewContainer.classList.add(`${this.styleNamespace}__new-message__file--hidden`)
    })

    function autoResize() {
      if (this.scrollHeight > 100) {
        this.style.height = '100px'
        return
      }

      this.style.height = 'auto'
      this.style.height = this.scrollHeight + 'px'
    }

    const emojiContainer = this.$element.querySelector(`.${this.styleNamespace}__new-message__emoji--container`)
    const emojiButton = this.$element.querySelector('.cce-widget-chat__new-message__emoji')
    let textarea = this.$element.querySelector('.cce-widget-chat__new-message textarea')

    emojiButton.addEventListener('click', (_) => {
      if (emojiContainer.classList.contains('show')) {
        emojiContainer.style.display = 'none'
        emojiContainer.classList.remove('show')
      } else {
        emojiContainer.style.display = 'block'
        emojiContainer.classList.add('show')
      }
    })

    textarea.addEventListener('input', autoResize, false)

    textarea.addEventListener('input', () => this.sendTypingIndicator(), false)

    textarea.addEventListener('keyup', (e) => {
      if (e.currentTarget.value != '') {
        e.currentTarget.parentElement.setAttribute('good', '')
      } else {
        e.currentTarget.parentElement.removeAttribute('good')
      }
    })

    const emojiPicker = this.$element.querySelector('emoji-picker')

    emojiPicker.addEventListener('emoji-click', (event) => {
      let emoji = event.detail.unicode
      textarea.value += emoji
      textarea.dispatchEvent(new Event('input'))
    })
    emojiPicker.addEventListener('blur', () => {
      emojiContainer.style.display = 'none'
    })

    textarea.addEventListener('click', (_) => {
      emojiContainer.style.display = 'none'
    })
    this.$element.querySelector('.cce-widget-chat__messages').addEventListener('click', (_) => {
      emojiContainer.style.display = 'none'
    })

    emojiPicker.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        emojiContainer.style.display = 'none'
      }
    })

    this.$element.querySelector('.cce-widget-chat__new-message textarea').addEventListener('keydown', (e) => {
      if (e.defaultPrevented) return
      if (this.isMobile) return
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        e.stopPropagation()
        this.handleFormSubmit()
      }
    })

    if (this.clearConversationEnabled) {
      const clearHistoryButton = document.querySelector('.cce-widget__window__header--menuButton-clear')

      clearHistoryButton.classList.remove('cce-widget__window__header--menuButton-hide')

      clearHistoryButton.addEventListener('click', () => {
        const chatUpgrade = document.querySelector('.cce-widget__window__header-chat-upgrade')
        if (chatUpgrade) return
        const windowContainer = document.querySelector('.cce-widget__window__container')

        const overlay = document.createElement('div')
        overlay.classList.add('cce-widget__window__clear-history__overlay')

        const clearHistoryForm = document.createElement('div')
        clearHistoryForm.classList.add('cce-cce-widget__clear-history')

        const formHTML = `
              <form class="cce-widget__clear-history-form" novalidate>
                <span class="cce-widget__clear-history-form-title">${this.texts.clearHistoryDescription}</span>
                <div class="cce-widget__clear-history-form-buttons">
                    <button class="cce-widget__clear-history-close">${this.texts.clearHistoryCancel}</button>
                    <button type="submit" class="cce-widget__clear-history-button">${this.texts.clearHistorySubmit}</button>
                </div>
              </form>`

        clearHistoryForm.innerHTML = formHTML

        windowContainer.appendChild(overlay)
        windowContainer.appendChild(clearHistoryForm)

        const closeButton = clearHistoryForm.querySelector('.cce-widget__clear-history-close')

        if (closeButton) {
          closeButton.addEventListener('click', () => {
            windowContainer.removeChild(clearHistoryForm)
            windowContainer.removeChild(overlay)
          })
        }

        if (clearHistoryForm) {
          clearHistoryForm.addEventListener('submit', async (event) => {
            event.preventDefault()
            await this.clearConversation()
            windowContainer.removeChild(clearHistoryForm)
            windowContainer.removeChild(overlay)

            const notificationHTML = `
            <div class="cce-widget__window__notification-clear-history">
              <div class="cce-widget__window__notification-clear-history-icon">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5.00002 16C4.99972 14.0689 5.50777 12.1718 6.47309 10.4994C7.43842 8.82693 8.82701 7.43806 10.4993 6.47241C12.1715 5.50675 14.0685 4.99833 15.9996 4.99826C17.9306 4.99818 19.8277 5.50645 21.5 6.47198C21.7296 6.6025 22.0014 6.63692 22.2563 6.56773C22.5111 6.49854 22.7283 6.33136 22.8603 6.10267C22.9923 5.87399 23.0286 5.60236 22.9611 5.34706C22.8936 5.09176 22.7278 4.87353 22.5 4.73998C20.0217 3.30914 17.1406 2.73602 14.3034 3.1095C11.4662 3.48298 8.8315 4.78218 6.80793 6.80563C4.78435 8.82907 3.48498 11.4637 3.11132 14.3008C2.73766 17.138 3.31059 20.0192 4.74127 22.4976C6.17195 24.976 8.38042 26.913 11.0242 28.0083C13.6679 29.1037 16.5992 29.2961 19.3635 28.5557C22.1277 27.8153 24.5704 26.1835 26.3127 23.9134C28.0551 21.6432 28.9997 18.8617 29 16C29 15.7348 28.8947 15.4804 28.7071 15.2929C28.5196 15.1053 28.2652 15 28 15C27.7348 15 27.4804 15.1053 27.2929 15.2929C27.1054 15.4804 27 15.7348 27 16C27 18.9174 25.8411 21.7153 23.7782 23.7782C21.7153 25.8411 18.9174 27 16 27C13.0826 27 10.2847 25.8411 8.22184 23.7782C6.15894 21.7153 5.00002 18.9174 5.00002 16Z" fill="white"/>
                    <path d="M30.7073 6.70804C30.8003 6.61507 30.874 6.50469 30.9244 6.38321C30.9747 6.26173 31.0006 6.13153 31.0006 6.00004C31.0006 5.86855 30.9747 5.73835 30.9244 5.61688C30.874 5.4954 30.8003 5.38502 30.7073 5.29204C30.6143 5.19907 30.504 5.12531 30.3825 5.075C30.261 5.02468 30.1308 4.99878 29.9993 4.99878C29.8678 4.99878 29.7376 5.02468 29.6161 5.075C29.4947 5.12531 29.3843 5.19907 29.2913 5.29204L15.9993 18.586L10.7073 13.292C10.6143 13.1991 10.504 13.1253 10.3825 13.075C10.261 13.0247 10.1308 12.9988 9.99931 12.9988C9.86782 12.9988 9.73762 13.0247 9.61614 13.075C9.49466 13.1253 9.38429 13.1991 9.29131 13.292C9.19833 13.385 9.12458 13.4954 9.07426 13.6169C9.02395 13.7384 8.99805 13.8686 8.99805 14C8.99805 14.1315 9.02395 14.2617 9.07426 14.3832C9.12458 14.5047 9.19833 14.6151 9.29131 14.708L15.2913 20.708C15.3842 20.8012 15.4946 20.8751 15.616 20.9255C15.7375 20.9759 15.8678 21.0018 15.9993 21.0018C16.1308 21.0018 16.2611 20.9759 16.3826 20.9255C16.5041 20.8751 16.6144 20.8012 16.7073 20.708L30.7073 6.70804Z" fill="white"/>
                </svg>
              </div>
              <div class="cce-widget__window__notification-clear-history-description">
               ${this.texts.clearHistoryComplete}
              </div>
            </div>`

            this.showNotification(notificationHTML)
          })
        }
      })
    }

    if (!this.clearConversationEnabled) {
      document.querySelector('.cce-widget__window__header--menuButton-clear').remove()
    }

    if (this.showChatTranscript) {
      const transcriptButton = document.querySelector('.cce-widget__window__header--menuButton-transcript')

      transcriptButton.classList.remove('cce-widget__window__header--menuButton-hide')

      transcriptButton.addEventListener('click', () => {
        const chatUpgrade = document.querySelector('.cce-widget__window__header-chat-upgrade')
        if (chatUpgrade) return
        const windowContainer = document.querySelector('.cce-widget__window__container')

        const overlay = document.createElement('div')
        overlay.classList.add('cce-widget__window__transcript__overlay')

        const transcriptEmailForm = document.createElement('div')
        transcriptEmailForm.classList.add('cce-widget__transcript')

        const formHTML = `
              <form class="cce-widget__transcript__email-form" novalidate>
                <span class="cce-widget__transcript__email-form-title">${this.texts.transcriptDescription}</span>
                <div class="cce-widget__transcript__email-input-wrapper">
                    <label class="cce-widget__transcript__email-label">${this.texts.transcriptEmailInputLabel}</label>
                    <input type="email" id="transcript__email" name="transcript__email" class="cce-widget__transcript__email-input" required>
                </div>
                <div class="cce-widget__transcript__email-form-buttons">
                    <button class="cce-widget__transcript-close">${this.texts.transcriptClose}</button>
                    <button type="submit" class="cce-widget__transcript__email-button" disabled>${this.texts.transcriptSubmit}</button>
                </div>
              </form>`

        transcriptEmailForm.innerHTML = formHTML

        windowContainer.appendChild(overlay)
        windowContainer.appendChild(transcriptEmailForm)

        const closeSvg = transcriptEmailForm.querySelector('.cce-widget__transcript-close')
        const transcriptForm = transcriptEmailForm.querySelector('.cce-widget__transcript__email-form')
        const emailInput = transcriptEmailForm.querySelector('.cce-widget__transcript__email-input')
        const emailSendButton = transcriptEmailForm.querySelector('.cce-widget__transcript__email-button')

        if (closeSvg) {
          closeSvg.addEventListener('click', () => {
            windowContainer.removeChild(transcriptEmailForm)
            windowContainer.removeChild(overlay)
          })
        }

        if (emailInput) {
          emailInput.addEventListener('input', (event) => {
            event.preventDefault()
            transcriptForm.classList.remove('cce-widget__transcript__email-form-error')
            emailSendButton.disabled = event.target.value.length <= 3
          })
        }
        if (transcriptForm) {
          transcriptForm.addEventListener('submit', (event) => {
            event.preventDefault()
            const emailPattern = /^[\w.-]+(?:\+[\w]+)?@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}$/g
            const emailValue = emailInput.value

            if (!emailPattern.test(emailValue)) {
              transcriptForm.classList.add('cce-widget__transcript__email-form-error')

              const previousError = transcriptEmailForm.querySelector('.cce-widget__transcript__email-form-errorMessage')
              if (!previousError) {
                const errorMessage = document.createElement('span')
                errorMessage.classList.add('cce-widget__transcript__email-form-errorMessage')
                errorMessage.textContent = this.texts.transcriptInvalidEmail
                emailInput.insertAdjacentElement('afterend', errorMessage)
              }
              emailSendButton.disabled = true
            } else {
              transcriptForm.classList.remove('error')
              const accountId = this.widgetContainer.settings.id
              const deploymentId = this.deploymentId
              let lang = 'en'

              try {
                const activeWidget = this.widgetContainer.settings.widgets[this.widgetIndex]
                lang = activeWidget.settings.locale
              } catch (e) {}

              const postBody = JSON.stringify({
                email: emailValue,
                account_id: accountId,
                deployment_id: deploymentId,
                lang: lang,
                timeOffset: new Date().getTimezoneOffset(),
              })

              fetch(`${this.genesysWebMessengerHistoryService.host}/transcript/${this.token}`, {
                method: 'POST',
                body: postBody,
                headers: {
                  'Content-Type': 'application/json',
                },
              }).then((response) => console.log(response))

              windowContainer.removeChild(transcriptEmailForm)
              windowContainer.removeChild(overlay)

              const notificationHTML = `
              <div class="cce-widget__window__notification-clear-history">
                <div class="cce-widget__window__notification-clear-history-icon">
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5.00002 16C4.99972 14.0689 5.50777 12.1718 6.47309 10.4994C7.43842 8.82693 8.82701 7.43806 10.4993 6.47241C12.1715 5.50675 14.0685 4.99833 15.9996 4.99826C17.9306 4.99818 19.8277 5.50645 21.5 6.47198C21.7296 6.6025 22.0014 6.63692 22.2563 6.56773C22.5111 6.49854 22.7283 6.33136 22.8603 6.10267C22.9923 5.87399 23.0286 5.60236 22.9611 5.34706C22.8936 5.09176 22.7278 4.87353 22.5 4.73998C20.0217 3.30914 17.1406 2.73602 14.3034 3.1095C11.4662 3.48298 8.8315 4.78218 6.80793 6.80563C4.78435 8.82907 3.48498 11.4637 3.11132 14.3008C2.73766 17.138 3.31059 20.0192 4.74127 22.4976C6.17195 24.976 8.38042 26.913 11.0242 28.0083C13.6679 29.1037 16.5992 29.2961 19.3635 28.5557C22.1277 27.8153 24.5704 26.1835 26.3127 23.9134C28.0551 21.6432 28.9997 18.8617 29 16C29 15.7348 28.8947 15.4804 28.7071 15.2929C28.5196 15.1053 28.2652 15 28 15C27.7348 15 27.4804 15.1053 27.2929 15.2929C27.1054 15.4804 27 15.7348 27 16C27 18.9174 25.8411 21.7153 23.7782 23.7782C21.7153 25.8411 18.9174 27 16 27C13.0826 27 10.2847 25.8411 8.22184 23.7782C6.15894 21.7153 5.00002 18.9174 5.00002 16Z" fill="white"/>
                        <path d="M30.7073 6.70804C30.8003 6.61507 30.874 6.50469 30.9244 6.38321C30.9747 6.26173 31.0006 6.13153 31.0006 6.00004C31.0006 5.86855 30.9747 5.73835 30.9244 5.61688C30.874 5.4954 30.8003 5.38502 30.7073 5.29204C30.6143 5.19907 30.504 5.12531 30.3825 5.075C30.261 5.02468 30.1308 4.99878 29.9993 4.99878C29.8678 4.99878 29.7376 5.02468 29.6161 5.075C29.4947 5.12531 29.3843 5.19907 29.2913 5.29204L15.9993 18.586L10.7073 13.292C10.6143 13.1991 10.504 13.1253 10.3825 13.075C10.261 13.0247 10.1308 12.9988 9.99931 12.9988C9.86782 12.9988 9.73762 13.0247 9.61614 13.075C9.49466 13.1253 9.38429 13.1991 9.29131 13.292C9.19833 13.385 9.12458 13.4954 9.07426 13.6169C9.02395 13.7384 8.99805 13.8686 8.99805 14C8.99805 14.1315 9.02395 14.2617 9.07426 14.3832C9.12458 14.5047 9.19833 14.6151 9.29131 14.708L15.2913 20.708C15.3842 20.8012 15.4946 20.8751 15.616 20.9255C15.7375 20.9759 15.8678 21.0018 15.9993 21.0018C16.1308 21.0018 16.2611 20.9759 16.3826 20.9255C16.5041 20.8751 16.6144 20.8012 16.7073 20.708L30.7073 6.70804Z" fill="white"/>
                    </svg>
                </div>
                <div class="cce-widget__window__notification-clear-history-description">
                   ${this.texts.transcriptComplete}
                </div>
              </div>`

              this.showNotification(notificationHTML)
            }
          })
        }
      })
    }
    if (!this.showChatTranscript) {
      document.querySelector('.cce-widget__window__header--menuButton-transcript').remove()
    }

    if (this.preInteractionFormEnabled && !this.preInteractionFormExist) {
      this.$element.querySelector(`.cce-widget-chat__pre-interaction-form`).addEventListener('submit', (event) => {
        event.preventDefault()
        event.stopPropagation()
        let formData = new FormData(event.target)
        let formValues = Object.fromEntries(formData.entries())
        if (!this.validatePreInteractionForm('chat', this.preInteractionFormIti.form, this.preInteractionFormTexts)) return
        Object.keys(formValues).forEach((key) => {
          if (key.startsWith('consents[')) {
            delete formValues[key]
          }
        })

        event.target.querySelectorAll("input[type='checkbox']").forEach((checkbox) => {
          let match = checkbox.name.match(/^consents\[(.+)\]$/)
          if (match) {
            if (!formValues.consents) formValues.consents = {}
            formValues.consents[match[1]] = checkbox.checked
          }
        })

        if (this.preInteractionFormIti.form) {
          formValues.phoneNumber = this.preInteractionFormIti.form.getNumber()
        }

        this.preInteractionFormData = formValues
        const stringValue = JSON.stringify(formValues)
        cookieConsentManager.setLocalStorageItem('cce-widget-chat-form-data', stringValue, 'necessary')

        this.preInteractionFormExist = true

        widgetManager.setWidgetTab('trueengage.chat')

        this.sendWelcomeMessage(formValues)
        this.$element.querySelector(`.cce-widget-chat__pre-interaction-form`).remove();
      })
    }
  }
}
